import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faCube } from "@fortawesome/free-solid-svg-icons";
import { faGithub } from "@fortawesome/free-brands-svg-icons";
import Chip from "@mui/material/Chip";
import { FeaturedLink, FeaturedProjectContent } from "../data/content";
import "../assets/styles/FeaturedProject.scss";

gsap.registerPlugin(ScrollTrigger);

interface FeaturedProjectProps {
  content: FeaturedProjectContent;
}

const linkIcon = (kind: FeaturedLink["kind"]) => {
  if (kind === "github") return faGithub;
  if (kind === "secondary") return faCube;
  return faArrowUpRightFromSquare;
};

// Wyróżniony projekt nad sekcją doświadczenia: opis, funkcje, stack, linki i galeria zrzutów.
function FeaturedProject({ content }: FeaturedProjectProps) {
  const rootRef = useRef<HTMLElement>(null);

  // Delikatne wejście tekstu i zrzutów przy przewijaniu; przy ograniczonym ruchu bez animacji.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>(".featured__reveal").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 32,
          duration: 0.75,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 88%",
            toggleActions: "play none none reverse",
          },
        });
      });
    }, root);

    return () => ctx.revert();
  }, []);

  const [main, ...rest] = content.images;

  return (
    <section className="featured-wrapper" id={content.id} ref={rootRef} aria-labelledby={`${content.id}-title`}>
      <div className="featured-container">
        <p className="featured__eyebrow">{content.eyebrow}</p>
        <h1 id={`${content.id}-title`}>{content.title}</h1>
        <p className="section-subtitle">{content.pitch}</p>

        <div className="featured__grid">
          <div className="featured__text featured__reveal">
            <h2>{content.problemHeading}</h2>
            <p>{content.problem}</p>

            <h2>{content.solutionHeading}</h2>
            <p>{content.solution}</p>

            <h2>{content.roleLabel}</h2>
            <p>{content.role}</p>

            <h2>{content.featuresHeading}</h2>
            <ul className="featured__features">
              {content.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>

            <div className="flex-chips featured__chips">
              <span className="chip-title">{content.techLabel}</span>
              <div className="chip-tags">
                {content.tech.map((t) => (
                  <Chip key={t} className="skill-chip" label={t} />
                ))}
              </div>
            </div>

            <div className="featured__actions">
              {content.links.map((l) => (
                <a
                  key={l.url}
                  className={`featured__btn featured__btn--${l.kind === "primary" ? "primary" : "secondary"}`}
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={linkIcon(l.kind)} aria-hidden="true" />
                  <span>{l.label}</span>
                  <span className="visually-hidden"> {content.newTabHint}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="featured__gallery" role="group" aria-label={content.galleryLabel}>
            {main ? (
              <figure className="featured__shot featured__shot--main featured__reveal">
                <img src={main.src} alt={main.alt} width={1280} height={720} loading="lazy" decoding="async" />
                <figcaption>{main.caption}</figcaption>
              </figure>
            ) : null}
            <div className="featured__shots">
              {rest.map((img) => (
                <figure key={img.src} className="featured__shot featured__reveal">
                  <img src={img.src} alt={img.alt} width={1280} height={800} loading="lazy" decoding="async" />
                  <figcaption>{img.caption}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FeaturedProject;
